import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AnimateOnScroll from '../common/animate';
import DataService from '../../services/data.service';
import LoaderOverlay from '../common/loader.component';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import './customer-reviews.css';

const CustomerReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalReviews, setTotalReviews] = useState(0);
  const perPage = 10;

  useEffect(() => {
    fetchReviews(1);
  }, []);

  const fetchReviews = async (pageNum) => {
    setLoading(true);
    try {
      const response = await DataService.getPublicReviews(pageNum, perPage);
      if (response.data && response.data.success) {
        if (pageNum === 1) {
          setReviews(response.data.data);
        } else {
          setReviews(prev => [...prev, ...response.data.data]);
        }
        setTotalReviews(response.data.total);
        setHasMore(response.data.hasMore);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReviews(nextPage);
  };

  const renderStars = (rating) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map(star => (
          <FontAwesomeIcon
            key={star}
            icon={faStar}
            className={star <= rating ? 'star-filled' : 'star-empty'}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <>
      <LoaderOverlay loading={loading} />
      <div>
        <Helmet>
          <title>Customer Reviews | Divine Blush Beauty | Regina SK</title>
          <meta name="title" content="Customer Reviews | Divine Blush Beauty | Regina SK" />
          <meta name="description" content="Read what our customers say about Divine Blush Beauty in Regina, SK. Real reviews from satisfied clients about our facial treatments, waxing, makeup, and hair services." />
          <meta name="keywords" content="Divine Blush Beauty reviews, beauty salon Regina reviews, customer testimonials Regina, facial reviews Regina, makeup artist reviews Regina SK" />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://www.divineblushbeauty.ca/reviews" />

          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.divineblushbeauty.ca/reviews" />
          <meta property="og:title" content="Customer Reviews | Divine Blush Beauty | Regina SK" />
          <meta property="og:description" content="Read what our customers say about Divine Blush Beauty. Real reviews from satisfied clients." />
          <meta property="og:image" content="https://www.divineblushbeauty.ca/images/bb.jpg" />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Customer Reviews | Divine Blush Beauty" />
          <meta name="twitter:description" content="Read what our customers say about Divine Blush Beauty." />
          <meta name="twitter:image" content="https://www.divineblushbeauty.ca/images/bb.jpg" />
        </Helmet>

        <section
          className="hero-wrap hero-wrap-2"
          style={{ backgroundImage: "url('images/reviews_banner.png')" }}
          data-stellar-background-ratio="0.5"
        >
          <div className="overlay" />
          <div className="container">
            <div className="row no-gutters slider-text js-fullheight align-items-center justify-content-center">
              <AnimateOnScroll className="col-md-9 text-center">
                <h1 className="mb-3 bread">Customer Reviews</h1>
                <p className="breadcrumbs">
                  <span className="mr-2"><Link to="/">Home</Link></span> Reviews
                </p>
              </AnimateOnScroll>
            </div>
          </div>
        </section>

        <section className="ftco-section">
          <div className="container">
            <div className="row justify-content-center mb-5">
              <div className="col-md-8 text-center">
                <h2 className="heading mb-3">What Our Customers Say</h2>
                <p className="sub-heading">
                  Read reviews from our valued clients and see why they trust Divine Blush Beauty for their beauty needs.
                </p>
                {totalReviews > 0 && (
                  <p className="text-muted">
                    Showing {reviews.length} of {totalReviews} reviews
                  </p>
                )}
              </div>
            </div>

            <div className="row">
              {reviews.length === 0 && !loading && (
                <div className="col-md-12 text-center py-5">
                  <p className="text-muted">No reviews yet. Be the first to leave a review!</p>
                </div>
              )}

              {reviews.map((review, index) => (
                <AnimateOnScroll key={review.id} className="col-md-6 mb-4">
                  <div className="review-card h-100">
                    <div className="review-header">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h5 className="customer-name mb-1">{review.customer_name}</h5>
                          <small className="text-muted">{formatDate(review.created_at)}</small>
                        </div>
                        {renderStars(review.rating)}
                      </div>
                      <h6 className="review-title">{review.review_title}</h6>
                    </div>
                    <div className="review-body">
                      <p className="review-text">{review.review}</p>
                    </div>
                  </div>
                </AnimateOnScroll>
              ))}
            </div>

            {hasMore && reviews.length > 0 && (
              <div className="row">
                <div className="col-md-12 text-center mt-4">
                  <button
                    className="btn btn-primary btn-lg px-5 py-3"
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More Reviews'}
                  </button>
                </div>
              </div>
            )}

            {!hasMore && reviews.length > 0 && (
              <div className="row">
                <div className="col-md-12 text-center mt-4">
                  <p className="text-muted">You've reached the end of all reviews</p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default CustomerReviews;
